import { useState, useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle, XCircle, Star, MapPin } from "@phosphor-icons/react";
import { changeLanguage, getCurrentLanguage } from "@shared/lib/i18n";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { PartnersCarousel } from "@shared/components/PartnersCarousel";

export const Route = createFileRoute("/public-landing")({
  component: PublicLandingPage,
});

const MOCK_LOCATIONS = [
  {
    name: "Bistro Urban",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80",
    logo: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=200&q=80",
    benefit: { ro: "Desert din partea casei", en: "Dessert on the house" },
    category: { ro: "Loc suuper", en: "Suuper place" }
  },
  {
    name: "Coffee Lab",
    image: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=800&q=80",
    logo: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=200&q=80",
    benefit: { ro: "O cafea gratuită", en: "One free coffee" },
    category: { ro: "Cafenea", en: "Coffee Shop" }
  },
  {
    name: "FitLife Gym",
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80",
    logo: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=200&q=80",
    benefit: { ro: "Abonament 1 + 1 Gratis", en: "1 + 1 Free Membership" },
    category: { ro: "Fitness", en: "Fitness" }
  },
  {
    name: "Nails & Co",
    image: "https://images.unsplash.com/photo-1632345031435-8727f6897d53?w=800&q=80",
    logo: "https://images.unsplash.com/photo-1604654894610-df63bc536371?w=200&q=80",
    benefit: { ro: "Mani + Pedi Cadou", en: "Mani + Free Pedi" },
    category: { ro: "Salon", en: "Salon" }
  },
  {
    name: "La Mama",
    image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&q=80",
    logo: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200&q=80",
    benefit: { ro: "Platou aperitiv inclus", en: "Appetizer platter included" },
    category: { ro: "Restaurant", en: "Restaurant" }
  }
];

function PublicLandingPage() {
  const { t } = useTranslation();
  const currentLang = getCurrentLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
  const stats = useQuery(api.partners.getLandingPageStats);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentLocationIndex((prev) => (prev + 1) % MOCK_LOCATIONS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLanguage = () => {
    changeLanguage(currentLang === "ro" ? "en" : "ro");
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header - simplu, fără card */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border' : 'bg-transparent'}`}>
        <div className="container mx-auto max-w-6xl px-3 sm:px-0 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center pt-1 gap-2 cursor-pointer">
            <img
              src="/suuper_logo.svg"
              alt={t("app.name")}
              className="h-5 sm:h-8 w-auto"
            />
            <span className="text-1xl sm:text-2xl text-text">Iași</span>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/auth/login"
              className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-text-light hover:text-text transition-colors whitespace-nowrap"
            >
              {t("auth:login.title")}
            </Link>
            <Link
              to="/auth/register"
              className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium bg-primary-gradient text-white rounded-lg hover:shadow-lg hover:shadow-primary/30 transition-all whitespace-nowrap"
            >
              {t("auth:register.title")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section - cu device mockup */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/6 via-background to-background" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] bg-primary/20 blur-3xl rounded-full" />

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8 lg:gap-12 items-center">
            {/* Left Column - Text Content (first on mobile) */}
            <motion.div
              initial="initial"
              animate="animate"
              variants={fadeInUp}
              className="text-center md:text-left order-1 lg:order-1"
            >
              <motion.span
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Star weight="fill" className="w-4 h-4" />
                {currentLang === "ro" ? "Doar pe bază de invitație" : "Invitation only"}
              </motion.span>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-text mb-6 tracking-tight">
                {currentLang === "ro" ? (
                  <>
                    Locații <span className="text-primary">suuper</span>.<br />
                    <span className="text-primary">Beneficii garantate.</span>
                  </>
                ) : (
                  <>
                    Suuper places to go out.<br />
                    <span className="text-primary">Guaranteed benefits.</span>
                  </>
                )}
              </h1>

              <p className="text-lg md:text-xl text-text-muted mb-10 max-w-xl mx-auto md:mx-0">
                {currentLang === "ro"
                  ? "Obții beneficii instant, direct, la fața locului. Doar arăți beneficiul și te bucuri de experiențe suuper în cele mai faine locuri din oraș."
                  : "Get instant benefits, directly in the app, on the spot. Just scan and enjoy suuper experiences at the best places in town."}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 mb-8 lg:mb-0">
                <Link
                  to="/auth/register"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary-gradient text-white rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-primary/30 transition-all shadow-lg shadow-primary/25"
                >
                  {currentLang === "ro" ? "Vezi locațiile suuper" : "See the super places"}
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/auth/login"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-text rounded-xl font-semibold text-lg hover:bg-background-dark transition-colors border border-border"
                >
                  {currentLang === "ro" ? "Am deja cont" : "I have an account"}
                </Link>
              </div>
            </motion.div>

            {/* Right Column - Device Mockup (second on mobile) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative flex justify-center order-2 lg:order-2"
            >

              {/* Device Frame - iPhone style */}
              <div className="relative w-[260px] sm:w-[280px] lg:w-[320px] mx-auto">
                {/* Phone outer frame - silver/gray */}
                <div className="relative bg-gradient-to-b from-gray-200 via-gray-100 to-gray-200 rounded-[2.5rem] sm:rounded-[3rem] p-[5px] sm:p-[6px] shadow-2xl">
                  {/* Phone inner bezel - black */}
                  <div className="bg-white/100 rounded-[2.2rem] sm:rounded-[2.6rem] p-[2px] sm:p-[3px] relative">
                    {/* Dynamic Island / Notch */}
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 z-30">
                      <div className="w-16 h-6 bg-black/80 rounded-full flex items-center justify-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-gray-800" />
                        <div className="w-3 h-3 rounded-full bg-gray-800 ring-1 ring-gray-700" />
                      </div>
                    </div>

                    {/* Screen */}
                    <div className="relative bg-[#fff] rounded-[2rem] sm:rounded-[2.4rem] overflow-hidden" style={{ aspectRatio: "9/19", minHeight: "520px" }}>
                      {/* Status Bar */}
                      <div className="absolute top-0 left-0 right-0 h-10 sm:h-12 flex items-end justify-between px-4 sm:px-6 lg:px-8 pb-1 z-20 bg-white">
                        <span className="text-sm font-semibold text-gray-900">9:41</span>
                        <div className="flex items-center gap-1.5">
                          {/* Signal bars */}
                          <div className="flex items-end gap-0.5 h-3">
                            <div className="w-1 h-1.5 bg-gray-900 rounded-sm" />
                            <div className="w-1 h-2 bg-gray-900 rounded-sm" />
                            <div className="w-1 h-2.5 bg-gray-900 rounded-sm" />
                            <div className="w-1 h-3 bg-gray-900 rounded-sm" />
                          </div>
                          {/* Battery */}
                          <div className="flex items-center gap-0.5">
                            <div className="w-6 h-3 border-2 border-gray-900 rounded-sm flex items-center p-0.5">
                              <div className="w-full h-full bg-gray-900 rounded-sm" />
                            </div>
                            <div className="w-0.5 h-1.5 bg-gray-900 rounded-r-sm" />
                          </div>
                        </div>
                      </div>

                      {/* App Header - "Locație" centered with back arrow */}
                      <div className="absolute top-10 sm:top-12 left-0 right-0 h-10 sm:h-12 flex items-center px-3 sm:px-4 lg:px-5 z-20 bg-white border-b border-gray-100 shadow-sm rounded-b-[1.25rem] sm:rounded-b-[1.75rem]">
                        <ArrowRight size={18} className="sm:w-[22px] sm:h-[22px] text-gray-900 rotate-180 flex-shrink-0" weight="bold" />
                        <p className="flex-1 text-center text-primary text-xs sm:text-sm lg:text-base font-bold text-gray-900 -ml-3 sm:-ml-5 flex items-center justify-center gap-1 sm:gap-2">
                          {currentLang === "ro" ? "Locație" : "Location"}
                          <img src="/suuper_logo.svg" alt="Suuper" className="h-4 sm:h-5 lg:h-6 inline-block" />
                        </p>
                      </div>

                      {/* Restaurant Image - edge to edge, full height */}
                      <div className="absolute top-[0px] sm:top-[0px] left-0 right-0 bottom-0 overflow-hidden bg-gray-100 z-10">
                        <AnimatePresence mode="wait">
                          <motion.img
                            key={`bg-${currentLocationIndex}`}
                            initial={{ opacity: 0, filter: "blur(20px)", scale: 1.1 }}
                            animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
                            exit={{ opacity: 0, filter: "blur(20px)" }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            src={MOCK_LOCATIONS[currentLocationIndex].image}
                            alt="Restaurant"
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        </AnimatePresence>
                        {/* Gradient overlay pentru depth */}
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30" />
                      </div>

                      {/* Main White Card - Bottom Sheet */}
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[1.25rem] sm:rounded-t-[1.75rem] shadow-2xl z-10"
                        initial={{ y: 20 }}
                        animate={{ y: 0 }}
                      >
                        <div className="px-3 sm:px-4 lg:px-5 pt-3 sm:pt-4 lg:pt-5 pb-2 sm:pb-3 lg:pb-4">
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={`content-${currentLocationIndex}`}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.4 }}
                            >
                              {/* Restaurant Info - logo + name */}
                              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-lg relative bg-gray-50">
                                  <img
                                    src={MOCK_LOCATIONS[currentLocationIndex].logo}
                                    alt="Logo"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 leading-tight truncate">
                                    {MOCK_LOCATIONS[currentLocationIndex].name}
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <MapPin size={12} className="sm:w-[14px] sm:h-[14px] text-emerald-500 flex-shrink-0" weight="fill" />
                                    <span className="text-xs sm:text-sm text-gray-500 truncate">
                                      {MOCK_LOCATIONS[currentLocationIndex].category[currentLang as "ro" | "en"]}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Beneficiu activ */}
                              <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                                <motion.div
                                  animate={{ scale: [1, 1.15, 1] }}
                                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                                  className="flex-shrink-0"
                                >
                                  <CheckCircle size={20} className="sm:w-[24px] sm:h-[24px] lg:w-[26px] lg:h-[26px] text-emerald-500" weight="fill" />
                                </motion.div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs sm:text-sm lg:text-[15px] font-bold text-gray-900 leading-tight">
                                    {currentLang === "ro" ? "Primești azi" : "You get today"}
                                  </p>
                                  <p className="text-xs sm:text-sm lg:text-[15px] text-gray-600 mt-0.5">
                                    {MOCK_LOCATIONS[currentLocationIndex].benefit[currentLang as "ro" | "en"]}
                                  </p>
                                </div>
                              </div>

                              {/* CTA Button - violet/purple */}
                              <motion.button
                                className="w-full py-2 sm:py-2.5 lg:py-3.5 bg-[#7C5CFF] hover:bg-[#6B4FE0] text-white rounded-xl sm:rounded-2xl font-semibold text-xs sm:text-sm lg:text-[15px] shadow-lg shadow-[#7C5CFF]/25 mb-2 sm:mb-3 lg:mb-5"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                {currentLang === "ro" ? "Deblochează beneficiul" : "Unlock benefit"}
                              </motion.button>
                            </motion.div>
                          </AnimatePresence>
                        </div>


                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partners Carousel */}
      {stats?.carouselPartners && stats.carouselPartners.length > 0 && (
        <section className="py-12 bg-background">
          <div className="mb-8 px-3 sm:px-0">
            <div className="container mx-auto max-w-6xl">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-text">
                {currentLang === "ro" ? (
                  <>
                    Locații <span className="text-primary font-bold">suuper</span> din Iași
                  </>
                ) : (
                  <>
                    <span className="text-primary font-bold">suuper</span> partners in Iași
                  </>
                )}
              </h2>
            </div>
          </div>
          <PartnersCarousel partners={stats.carouselPartners} />
        </section>
      )}

      {/* Ce este un Loc suuper - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4 bg-white/50 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-8 text-center flex items-center justify-center gap-2 flex-wrap">
              {currentLang === "ro" ? (
                <>
                  Într-o locație <span className="text-primary font-bold">suuper</span> merită să intri
                </>
              ) : (
                <>
                  It's <span className="text-primary font-bold">suuper</span>, so it's a suuper place. Worth entering!
                </>
              )}
            </h2>
            <p className="text-xl text-text mb-10 text-center max-w-2xl mx-auto">
              {currentLang === "ro"
                ? "E simplu: dacă vezi badge-ul suuper, știi că primești ceva."
                : "It's simple: if you see the suuper badge, you know you're getting something."}
            </p>

            {/* Badge image */}
            <div className="flex justify-center mb-10">
              <img
                src="/badge_locație_suuper.ro.svg"
                alt={currentLang === "ro" ? "Badge locație suuper" : "Suuper location badge"}
                className="h-24 sm:h-32 w-auto"
              />
            </div>

            <div className="bg-gradient-to-br from-background to-white rounded-3xl p-8 sm:p-10 mb-8 shadow-xl border border-border/50">
              <div className="space-y-6">
                {[
                  { text: currentLang === "ro" ? "Locul e verificat de noi" : "The place is verified by us" },
                  { text: currentLang === "ro" ? "Beneficiul e real, nu 5% la nota de plată" : "The benefit is real, not 5% off the bill" },
                  { text: currentLang === "ro" ? "Nu trebuie să explici nimic, doar arăți telefonul" : "You don't have to explain anything, just show your phone" }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <div className="w-8 h-8 bg-success/20 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle size={20} className="text-success" />
                    </div>
                    <p className="text-lg text-text pt-1">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-l-4 border-primary rounded-2xl p-8 shadow-lg"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <p className="text-2xl font-bold text-text italic text-center">
                {currentLang === "ro"
                  ? "\"Intru aici, sigur iau ceva mișto.\""
                  : "\"I go in here, I definitely get something cool.\""}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Înainte vs. În locație - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-background to-white/50" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-text mb-16 text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            {currentLang === "ro"
              ? <>Cum functioneaza <span className="text-primary font-bold">suuper</span></>
              : <>How <span className="text-primary font-bold">suuper</span> works</>}
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="bg-gradient-to-br from-white to-background rounded-3xl p-8 border border-border shadow-xl hover:shadow-2xl transition-shadow"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-text mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <span className="text-primary font-bold">1</span>
                </div>
                {currentLang === "ro" ? "De acasă" : "From home"}
              </h3>
              <p className="text-text-light mb-6 font-medium">
                {currentLang === "ro" ? "Vezi:" : "You see:"}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  currentLang === "ro" ? "Numele și categoria (restaurant, cafenea, etc.)" : "Name and category (restaurant, cafe, etc.)",
                  currentLang === "ro" ? "Badge-ul suuper = beneficiu garantat" : "Suuper badge = guaranteed benefit",
                  currentLang === "ro" ? "Unde e pe hartă" : "Where it is on the map"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-text">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-6 border-t border-border">
                <p className="text-text-light mb-4 font-medium">
                  {currentLang === "ro" ? "Nu vezi încă:" : "You don't see yet:"}
                </p>
                <ul className="space-y-3">
                  {[
                    currentLang === "ro" ? "Ce anume primești" : "What exactly you get",
                    currentLang === "ro" ? "Asta se deblochează în locație" : "That unlocks at the location"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <XCircle size={20} className="text-text-muted flex-shrink-0 mt-0.5" />
                      <span className="text-text">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="bg-gradient-to-br from-primary/5 via-white to-background rounded-3xl p-8 border-2 border-primary/20 shadow-xl hover:shadow-2xl transition-shadow"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-text mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold">2</span>
                </div>
                {currentLang === "ro" ? "Când ajungi acolo" : "When you get there"}
              </h3>
              <p className="text-text-light mb-6 font-medium">
                {currentLang === "ro" ? "În locație:" : "At the location:"}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  currentLang === "ro" ? "Scanezi codul QR din locație" : "You scan the QR code at the location",
                  currentLang === "ro" ? "Beneficiul se deblochează automat" : "The benefit unlocks automatically",
                  currentLang === "ro" ? "Arăți telefonul personalului locației" : "You show your phone to the location staff",
                  currentLang === "ro" ? "Gata. Primești ce ți se cuvine." : "Done. You get what you deserve."
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-text font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-primary/10 border-l-4 border-primary rounded-xl p-6 mt-6">
                <p className="text-text font-bold italic text-lg">
                  {currentLang === "ro"
                    ? "\"Oricum voiam să vin aici. Bonus.\""
                    : "\"I wanted to come here anyway. Bonus.\""}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Beneficiu real - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4 bg-gradient-to-b from-white/50 via-background to-white/50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-text mb-8 text-center">
              {currentLang === "ro" ? "Ce poți primi" : "What you can get"}
            </h2>
            <p className="text-xl text-text mb-12 text-center max-w-3xl mx-auto">
              {currentLang === "ro"
                ? "Nu e vorba de reduceri simbolice. E vorba de chestii care chiar contează."
                : "It's not about symbolic discounts. It's about things that actually matter."}
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <motion.div
                className="bg-white rounded-2xl p-8 border border-border shadow-lg hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold text-text mb-4 flex items-center gap-2">
                  <CheckCircle size={24} className="text-success" />
                  {currentLang === "ro" ? "Ce beneficii primești" : "What benefits you get"}
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      title: currentLang === "ro" ? "29 lei / lună - Horeca" : "29 lei / month - Horeca",
                      items: [
                        currentLang === "ro" ? "Restaurante: desert, băutură sau aperitiv gratuit" : "Restaurants: dessert, drink or free appetizer",
                        currentLang === "ro" ? "Cafenele: băutură inclusă sau upgrade la porție" : "Cafes: drink included or portion upgrade",
                        currentLang === "ro" ? "Rezervări garantate sau loc preferențial" : "Guaranteed reservations or preferred seating"
                      ]
                    },
                    {
                      title: currentLang === "ro" ? "49 lei / lună - Toate categoriile" : "49 lei / month - All categories",
                      items: [
                        currentLang === "ro" ? "Tot ce e în abonamentul Horeca, plus:" : "Everything in Horeca subscription, plus:",
                        currentLang === "ro" ? "Fitness: sesiune gratuită sau reducere la abonament" : "Fitness: free session or subscription discount",
                        currentLang === "ro" ? "Wellness: tratament inclus sau upgrade servicii" : "Wellness: treatment included or service upgrade",
                        currentLang === "ro" ? "Alte categorii: beneficii specifice fiecărui partener" : "Other categories: benefits specific to each partner"
                      ]
                    }
                  ].map((category, idx) => (
                    <div key={idx} className="pb-4 border-b border-border last:border-0 last:pb-0">
                      <h4 className="font-semibold text-text mb-2">{category.title}</h4>
                      <ul className="text-text-light space-y-1 text-sm">
                        {category.items.map((item, itemIdx) => (
                          <li key={itemIdx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                className="bg-gradient-to-br from-error/5 to-error/10 rounded-2xl p-8 border-2 border-error/20 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="text-xl font-bold text-text mb-4 flex items-center gap-2">
                  <XCircle size={24} className="text-error" />
                  {currentLang === "ro" ? "Ce NU vei găsi pe Suuper" : "What you WON'T find on Suuper"}
                </h3>
                <ul className="space-y-3 text-text">
                  {[
                    currentLang === "ro" ? "Reduceri de 5-10% (nu merită deranjul)" : "5-10% discounts (not worth the hassle)",
                    currentLang === "ro" ? "Oferte complicate cu asteriscuri" : "Complicated offers with asterisks",
                    currentLang === "ro" ? "\"Arată-mi aplicația și il intreb pe seful\"" : "\"Show me the app and I'll ask the boss\"",
                    currentLang === "ro" ? "Beneficii pe care nu le-ai folosi oricum" : "Benefits you wouldn't use anyway"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <XCircle size={20} className="text-error flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            <motion.div
              className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-l-4 border-primary rounded-2xl p-8 shadow-lg"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <p className="text-xl font-bold text-text italic text-center">
                {currentLang === "ro"
                  ? "Dacă nu e ceva ce te-ar bucura, nu intră pe Suuper."
                  : "If it's not something that would make you happy, it doesn't go on Suuper."}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Cum funcționează - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="container mx-auto max-w-6xl relative z-10 text-center">
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-text mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            {currentLang === "ro" ? (
              <>
                3 pași. Atât. E{" "}
                <span className="text-primary font-bold">suuper</span>
              </>
            ) : (
              "3 steps. That's it."
            )}
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              { step: "1", text: currentLang === "ro" ? "Alegi un loc suuper" : "Pick a suuper place" },
              { step: "2", text: currentLang === "ro" ? "Mergi acolo" : "Go there" },
              { step: "3", text: currentLang === "ro" ? "Arăți telefonul și primești" : "Show your phone and get it" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                className="bg-gradient-to-br from-white to-background rounded-3xl p-8 border border-border shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
              >
                <div className="text-5xl font-bold text-primary mb-6">{item.step}</div>
                <p className="text-xl text-text font-medium">{item.text}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-2xl text-text font-bold mb-2">
              {currentLang === "ro" ? "Fără explicații." : "No explanations."}
            </p>
            <p className="text-2xl text-text font-bold">
              {currentLang === "ro" ? "Funcționează și punct." : "It just works."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Acces & model - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4 bg-white/50">
        <div className="container mx-auto max-w-4xl">
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-text mb-12 text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            {currentLang === "ro" ? "Cât costă" : "How much does it cost"}
          </motion.h2>
          <motion.div
            className="bg-gradient-to-br from-background to-white rounded-3xl p-8 sm:p-10 mb-8 shadow-xl border border-border/50"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xl text-text mb-8 text-center font-medium">
              {currentLang === "ro"
                ? <>Abonamentele <img src="/suuper_logo.svg" alt="Suuper" className="inline align-middle h-6" /> sunt gândite pentru tine</>
                : <>Suuper memberships <img src="/suuper_logo.svg" alt="Suuper" className="inline align-middle h-6" /> are made for you</>}
            </p>
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="text-xl font-bold text-text mb-4">{currentLang === "ro" ? "Cum intri" : "How to get in"}</h3>
                <ul className="text-text-light space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{currentLang === "ro" ? "Primești o invitație de la cineva care e deja membru" : "You get an invite from someone who's already a member"}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{currentLang === "ro" ? "Sau inviți tu un prieten să intrați împreună" : "Or you invite a friend to join together"}</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="text-xl font-bold text-text mb-4">{currentLang === "ro" ? "Perioada de probă" : "Trial period"}</h3>
                <ul className="text-text-light space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{currentLang === "ro" ? "7 zile să testezi" : "7 days to try it"}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{currentLang === "ro" ? "Până la 5 beneficii, să vezi cum e" : "Up to 5 benefits, to see how it works"}</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-6 border-2 border-primary/20">
                <h3 className="text-xl font-bold text-text mb-4">{currentLang === "ro" ? "După perioada de probă" : "After trial period"}</h3>
                <ul className="text-text space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <span><strong className="text-primary">29 lei / lună</strong> — {currentLang === "ro" ? "restaurante și cafenele" : "restaurants and cafes"}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <span><strong className="text-primary">49 lei / lună</strong> — {currentLang === "ro" ? "toate categoriile (și fitness, wellness, etc.)" : "all categories (plus fitness, wellness, etc.)"}</span>
                  </li>
                </ul>
                <p className="text-sm text-text-muted mt-4">
                  {currentLang === "ro" ? "Ieși o dată pe săptămână? Deja ai recuperat banii." : "Go out once a week? You've already got your money back."}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pentru cine este - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-text mb-16 text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            {currentLang === "ro" ? "E pentru tine?" : "Is it for you?"}
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="bg-gradient-to-br from-success/10 to-success/5 border-2 border-success/20 rounded-3xl p-8 shadow-xl"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-text mb-6">
                {currentLang === "ro" ? "Da, dacă:" : "Yes, if:"}
              </h3>
              <ul className="space-y-4 text-text">
                {[
                  currentLang === "ro" ? "Îți place să fii tratat bine" : "You like being treated well!",
                  currentLang === "ro" ? "Îți place să descoperi locuri noi" : "You like discovering new places",
                  currentLang === "ro" ? "Apreciezi când primești ceva în plus, firesc" : "You appreciate getting a little extra, naturally"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={24} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="bg-gradient-to-br from-error/10 to-error/5 border-2 border-error/20 rounded-3xl p-8 shadow-xl"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-text mb-6">
                {currentLang === "ro" ? "Nu, dacă:" : "No, if:"}
              </h3>
              <ul className="space-y-4 text-text">
                {[
                  currentLang === "ro" ? "Cauți gratuit nelimitat (nu există)" : "You're looking for unlimited free (doesn't exist)",
                  currentLang === "ro" ? "Vrei 50% reducere la tot (nici asta)" : "You want 50% off everything (not that either)",
                  currentLang === "ro" ? "Nu prea ieși din casă" : "You don't really go out much"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <XCircle size={24} className="text-error flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4 bg-white/50">
        <div className="container mx-auto max-w-4xl">
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-text mb-12 text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            {currentLang === "ro" ? "Întrebări" : "Questions"}
          </motion.h2>
          <div className="space-y-4">
            {[
              {
                q: currentLang === "ro" ? "Trebuie să instalez ceva?" : "Do I need to install anything?",
                a: currentLang === "ro" ? "Nu. E o aplicație web. Intri, te loghezi, gata." : "No. It's a web app. You go in, log in, done."
              },
              {
                q: currentLang === "ro" ? "Văd de acasă ce primesc?" : "Can I see from home what I get?",
                a: currentLang === "ro" ? "Vezi unde poți merge și că vei primi ceva. Ce anume - afli când ajungi." : "You see where you can go and that you'll get something. What exactly - you find out when you arrive."
              },
              {
                q: currentLang === "ro" ? "Chiar primesc ceva de fiecare dată?" : "Do I really get something every time?",
                a: currentLang === "ro" ? "Da. Dacă locul e pe Suuper, primești. Garantat. Altfel nu ar fi pe Suuper." : "Yes. If the place is on Suuper, you get it. Guaranteed. Otherwise it wouldn't be on Suuper."
              },
              {
                q: currentLang === "ro" ? "Pot folosi și prietenii mei?" : "Can my friends use it too?",
                a: currentLang === "ro" ? "Fiecare beneficiu e pentru o vizită. Dar poți să-ți inviți prietenii să-și facă cont." : "Each benefit is for one visit. But you can invite your friends to make their own account."
              },
              {
                q: currentLang === "ro" ? "Ce se întâmplă dacă locul nu-mi dă beneficiul?" : "What if the place doesn't give me the benefit?",
                a: currentLang === "ro" ? "Scrie-ne. Rezolvăm noi cu ei. Dacă nu respectă, îi scoatem de pe platformă." : "Write to us. We'll handle it with them. If they don't comply, we remove them from the platform."
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                className="bg-white rounded-2xl p-6 sm:p-8 border border-border shadow-lg hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <h3 className="font-bold text-text mb-3 text-lg">{faq.q}</h3>
                <p className="text-text-light">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - îmbunătățit */}
      <section className="py-20 sm:py-28 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-primary/5 to-background" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-primary/20 blur-3xl rounded-full" />
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-text mb-6">
              {currentLang === "ro" ? (
                <>
                  Primești mai mult, mereu.<br />
                  E <span className="text-primary">suuper.</span>
                </>
              ) : (
                <>
                  Receive more, always.<br />
                  <span className="text-primary">It's suuper.</span> Sign in
                </>
              )}
            </h2>
            <Link
              to="/auth/register"
              className="inline-flex items-center gap-2 px-10 py-5 bg-primary-gradient text-white rounded-xl font-semibold text-xl hover:shadow-2xl hover:shadow-primary/40 transition-all shadow-xl shadow-primary/30"
            >
              {currentLang === "ro" ? "Vreau să intru" : "I want in"}
              <ArrowRight size={24} />
            </Link>
            <p className="text-sm text-text-muted mt-6">
              {currentLang === "ro" ? "Ai nevoie de o invitație" : "You need an invitation"}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer - identic cu index.tsx */}
      <footer className="py-12 px-4 bg-text">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Link to="/partners">
                <img
                  src="/suuper_logo_white.svg"
                  alt={t("app.name")}
                  className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
                />
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-6 text-white/60 text-sm">
                <a href="#" className="hover:text-white transition-colors">
                  {currentLang === "ro" ? "Termeni și condiții" : "Terms of Service"}
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  {currentLang === "ro" ? "Confidențialitate" : "Privacy Policy"}
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  {currentLang === "ro" ? "Contact" : "Contact"}
                </a>
              </div>

              <button
                onClick={toggleLanguage}
                className="px-3 py-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors rounded-md hover:bg-white/10 border border-white/20"
              >
                {currentLang === "ro" ? "EN" : "RO"}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
